import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearCountryDetails, closeCountryDrawerOpen } from "./countrySlice";
import {
  createCountry,
  getAllActiveCountries,
  getAllCountries,
  getCountryByID,
  updateCountry,
} from "./services/country.services";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import { Typography } from "@progress/kendo-react-common";

const CreateCountry: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryDetail = useAppSelector((state) => state.country.CountryDetail);
  const country_guid = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.country.formLoading);
  const FilterCountry = useAppSelector((state) => state.country.FilterCountry);
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const isIndirect = useAppSelector((state) => state.muidrawer.isIndirect);
  const countryDrawerOpen = useAppSelector(
    (state) => state.country.countryDrawerOpen
  );
  useEffect(() => {
    return () => {
      dispatch(clearCountryDetails());
      if (countryDrawerOpen) {
        dispatch(closeCountryDrawerOpen());
      } else {
        dispatch(closed());
      }
    };
  }, []);

  useEffect(() => {
    if (country_guid && !countryDrawerOpen) {
      dispatch(getCountryByID(country_guid));
    }
  }, [country_guid, countryDrawerOpen]);

  const payload = {
    ...FilterCountry,
    Limit: +pageLimit,
    Skip: +currentPage * +pageLimit,
  };
  const handleSubmit = async (values: any) => {
    try {
      if (!country_guid) {
        const insertPayload = {
          country_name: values.country_name,
          country_code: values.country_code,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(createCountry(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (countryDrawerOpen) {
            dispatch(closeCountryDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearCountryDetails());
          isIndirect || countryDrawerOpen
            ? dispatch(getAllActiveCountries())
            : dispatch(getAllCountries(payload));
        }
      } else {
        const editPayload = {
          country_guid: country_guid,
          country_name: values.country_name,
          country_code: values.country_code,
          isactive: values.isactive === 1,
        };
        const response = await dispatch(updateCountry(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (countryDrawerOpen) {
            dispatch(closeCountryDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearCountryDetails());
          isIndirect || countryDrawerOpen
            ? dispatch(getAllActiveCountries())
            : dispatch(getAllCountries(payload));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    // console.log("countryDrawerOpen", countryDrawerOpen);
    if (countryDrawerOpen) {
      dispatch(closeCountryDrawerOpen());
    } else {
      dispatch(closed());
    }
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={CountryDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <Typography.h4 className="m-0">
              {country_guid && !countryDrawerOpen
                ? "Update Country"
                : "Add Country"}
            </Typography.h4>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="country_name"
                label="Country Name"
                placeholder="i.e. India"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="country_code"
                label="Country Code"
                placeholder="i.e. IND"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                id={"IsActive"}
                name={"isactive"}
                component={FormSelectionField}
                validator={requiredValidator}
                astrike={true}
                label={"Status"}
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status.value,
                    label: status.label,
                  };
                })}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={country_guid && !countryDrawerOpen ? "Update" : "Create"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateCountry;
